import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Divider,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import dayjs, { Dayjs } from 'dayjs';
import DayjsUtils from '@date-io/dayjs';
import { Add, FolderOpenOutlined, Save } from '@material-ui/icons';
import Grid from '@material-ui/core/Grid';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import NavAppbar from '../nav-top/NavAppbar';
import CircularLoader from '../loader/CircularLoader';
import {
  FolderStructure,
  IAddress,
  ICustomInsertionProject,
  IProject,
  IProjectName,
  IProjectUser,
  ISubscriber,
  PhoneNumberType,
} from '../../api-client/autogenerated';
import {
  addOrModifyUsersOfProject,
  getSuggestedProjectEmailSlug,
  insertProject,
  patchProject,
} from '../../models/api/project';
import PageTitle from '../page-title/PageTitle';
import CreateProjectUsers from '../project-users/CreateProjectUsers';
import { insertUser, removeUserFromProjectByProjectId } from '../../models/api/users';
import { insertAddress } from '../../models/api/addresses';
import { getStateAndProvinceItems } from '../../bidding-components/utils';
import { getUserState } from '../../features/user/selectors';
import { fetchProjects } from '../../features/projects/actions';
import {
  getProjectState,
  getProjectUsersIncludingDisabledState,
} from '../../features/project/selectors';
import {
  fetchSecurityGroups,
  fetchSubscriberSecurityGroups,
} from '../../features/security/actions';
import FullscreenLoader from '../loader/FullscreenLoader';
import { useParams } from 'react-router';
import { parseDate, ProjectMatchParams, removeNewlines } from '../../scripts/utils';
import { fetchProject } from '../../features/project/actions';
import { insertPhoneNumberByUserId } from '../../models/api/phone-number';
import { SubmitButton } from '../custom-components/CustomButtons';
import { allowNavigation, blockNavigation } from '../../features/navigation/actions';
import {
  getAllProjectNamesBySubscriberId,
  getSubscriberById,
  getSubscriberFolderStructures,
  getSubscribers,
} from '../../models/api/subscribers';
import WorkingDaysSettingsFields from '../working-days/WorkingDaysSettingsFields';
import {
  draftFromWorkingDaySettings,
  getWorkingDaySettingsError,
  workingDaySettingsFromDraft,
  WorkingDaySettingsDraft,
} from '../../scripts/working-days';
import DefaultFolderStructureDialog from '../dialogs/DefaultFolderStructureDialog';
import { hasAdminPermissions } from '../../scripts/store-utils';
import { addSnackbar } from '../../features/snackbar/actions';
import { GreenCheck } from '../custom-components/CustomCheckboxes';
import { fetchUser } from '../../features/user/actions';
import { ARCHIVED_PROJECT_TOOLTIP, isProjectArchived } from '../../scripts/permission-utils';
import { TooltipIfDisabledComponent } from '../custom-components/CustomButtons';

const useStyles = makeStyles((theme: Theme) => ({
  form: {
    marginTop: 8,
    paddingBottom: 32,
  },
  submit: {
    margin: theme.spacing(1),
    lineHeight: 1,
  },
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
    minHeight: '100vh',
    overflowX: 'hidden',
  },
  paper: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    width: '100%',
  },
  alignLeft: {
    textAlign: 'left',
  },
  paddingLeft: {
    paddingLeft: 15,
    paddingTop: 10,
  },
  loadingCenter: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    width: '100%',
  },
  loadingNotCenter: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  loadingMargin: {
    paddingBottom: 16,
  },
  title: {
    padding: 0,
  },
  textField: {
    maxWidth: 300,
    paddingTop: 4,
    paddingBottom: 8,
  },
  // MuiPaper's elevation1 boxShadow is zeroed out globally (see customTheme.tsx), which makes
  // Autocomplete's default popup (elevation 1) blend into the page. Select's menu isn't affected
  // since it uses elevation 8. Reinstate a shadow here, matching ProjectAutocomplete's popper fix.
  clientPopper: {
    boxShadow: '3px 5px 5px 2px rgba(0, 0, 0, 0.20)',
  },
  textFieldSmall: {
    maxWidth: 142,
  },
  rootGrid: {
    maxWidth: 700,
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
  },
  formControl: {
    padding: 0,
  },
  fileInstructions: {
    maxWidth: 500,
  },
  stateRequiredPill: {
    display: 'flex',
    width: 'fit-content',
    alignItems: 'center',
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    borderRadius: 16,
    padding: theme.spacing(0.5, 1.5),
    marginBottom: theme.spacing(1),
    fontSize: 13,
    fontWeight: 500,
    lineHeight: '16px',
    whiteSpace: 'nowrap',
  },
  // Lines the pill up with a button that carries classes.submit's margin.
  stateRequiredPillInset: {
    marginLeft: theme.spacing(1),
  },
  endDiv: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 8,
    paddingBottom: 8,
  },
  widthConstraint: {
    maxWidth: 700,
  },
  placeholder: {
    fontStyle: 'italic',
  },
}));

interface AddOrManageProjectPageProps {
  edit?: boolean;
}

export enum UserActionType {
  None,
  // eslint-disable-next-line
  Add,
  Delete,
  Edit,
  Invite,
}

interface UserAndAction {
  user: IProjectUser;
  action: UserActionType;
}

function AddOrManageProjectPage(props: AddOrManageProjectPageProps) {
  const classes = useStyles();
  const history = useHistory();
  const params = useParams<ProjectMatchParams>();
  const dispatch = useDispatch();
  const user = useSelector(getUserState);
  const { edit } = props;
  const manageCurrentProject = useSelector(getProjectState);
  const currentProject = edit ? manageCurrentProject : null;

  const date = currentProject?.createdOn
    ? parseDate(currentProject.createdOn).format('MM/DD/YYYY')
    : dayjs().format('MM/DD/YYYY');
  // Adding 'Demo' here temporarily to let the project have the demo account as the project owner
  // Remove 'Demo' when issue #255 is resolved
  const projectOwner = currentProject ? currentProject.owner : user.name;

  const [isLoading, setIsLoading] = useState(false);

  const [projectName, setProjectName] = useState<string>();
  const [projectNameError, setProjectNameError] = useState<string>();
  const [projectDescription, setProjectDescription] = useState<string>();
  const [ownerProjectNumber, setOwnerProjectNumber] = useState<string>();
  const [architectProjectNumber, setArchitectProjectNumber] = useState<string>();
  const [client, setClient] = useState<string>();
  const [approvedDate, setApprovedDate] = useState<Dayjs | null>();
  const [address1, setAddress1] = useState('');
  const [cityName, setCityName] = useState('');
  // The name of the state, not its id -- that is what the picker's items carry, and what
  // insertAddress resolves to an id.
  const [stateLocation, setStateLocation] = useState('');
  const [emailSlug, setEmailSlug] = useState<string>();
  const [emailSlugError, setEmailSlugError] = useState<string>();
  const [areEmailsSuspended, setAreEmailsSuspended] = useState(false);
  const [hasDuplicateSlug, setHasDuplicateSlug] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');

  // shallowEqual so unrelated store updates (e.g. fetchSecurityGroups during submit)
  // don't change this array's reference and re-fire the rebuild effect with stale data,
  // which would wipe local Delete/Edit/Add marks before fetchProject resolves.
  // Includes disabled users on purpose: this list SHOWS them, greyed with a
  // [Disabled] label and with the selection checkbox switched off, so an admin
  // can see and remove them but never select them. Every other list filters.
  const projectUsers = useSelector(getProjectUsersIncludingDisabledState, shallowEqual);
  const [users, setUsers] = useState<UserAndAction[]>([]);

  const [existingProjects, setExistingProjects] = useState<IProjectName[]>([]);

  const [folderStructures, setFolderStructures] = useState<FolderStructure[]>([]);
  const [areFolderStructuresLoading, setAreFolderStructuresLoading] = useState(false);
  const [useDefaultFolderStructure, setUseDefaultFolderStructure] = useState(false);
  const [selectedFolderStructureId, setSelectedFolderStructureId] = useState('');
  const [isFolderStructureDialogOpen, setIsFolderStructureDialogOpen] = useState(false);

  const selectedFolderStructure = folderStructures.find(
    (structure) => structure.id === selectedFolderStructureId,
  );

  const [subscribers, setSubscribers] = useState<ISubscriber[]>([]);
  const [selectedSubscriberId, setSelectedSubscriberId] = useState('');
  const [changeSubscriberLoading, setChangeSubscriberLoading] = useState(false);

  // Only while creating: how this project's review deadlines are counted, pre-filled from the
  // subscriber's defaults and saved on the project so later subscriber-level edits don't rewrite
  // it. Stays null until the defaults load; the section renders once they have.
  const [workingDaySettings, setWorkingDaySettings] = useState<WorkingDaySettingsDraft | null>(
    null,
  );
  const workingDaySettingsError = workingDaySettings
    ? getWorkingDaySettingsError(workingDaySettings)
    : undefined;

  const canChangeSubscriber = !!user.isSiteAdmin && !!currentProject;

  // An archived project may only be restored or deleted, and neither happens on this page, so it
  // is treated as if the user had no admin rights here -- which is what already disables the
  // fields and the save below.
  const isArchivedProject = isProjectArchived(currentProject);

  const isAdminUser =
    !isArchivedProject &&
    (currentProject ? hasAdminPermissions() : !!user.adminOfSubscriberId || user.isSiteAdmin);

  // Shown above both Save buttons on an existing project that has no state, and only to users who
  // can set one (non-admins can't, and aren't blocked from saving because of it).
  const showStateRequired = !!currentProject && !stateLocation && !!isAdminUser;

  const hasDuplicateName =
    !!projectName &&
    existingProjects.some((p) => p.name === projectName.trim() && p.id !== currentProject?.id);

  const clientOptions = useMemo(
    () =>
      Array.from(
        new Set(existingProjects.map((p) => p.client).filter((c): c is string => !!c)),
      ).sort((a, b) => a.localeCompare(b)),
    [existingProjects],
  );

  const verifyEmailSlug = async () => {
    if (!projectName || !emailSlug) return;
    const suggestedSlug = await getSuggestedProjectEmailSlug(projectName, emailSlug);
    setHasDuplicateSlug(suggestedSlug !== emailSlug);
  };

  useEffect(() => {
    if (!manageCurrentProject) {
      if (user.adminOfSubscriberId) {
        dispatch(fetchSubscriberSecurityGroups(user.adminOfSubscriberId));
      }
    }
  }, [manageCurrentProject]);

  useEffect(() => {
    if (canChangeSubscriber) {
      getSubscribers(1000).then(setSubscribers);
    }
  }, [user, currentProject]);

  useEffect(() => {
    if (currentProject) {
      getAllProjectNamesBySubscriberId(currentProject.subscriberId).then(setExistingProjects);
    } else if (user.adminOfSubscriberId) {
      getAllProjectNamesBySubscriberId(user.adminOfSubscriberId).then(setExistingProjects);
    }
  }, [user, currentProject]);

  // Folder structures only apply to a project being created, and only the subscriber's admin has
  // any to apply. Start it checked, on the first structure, when the subscriber has set any up,
  // since that is what setting them up is for; subscribers without any are unaffected.
  useEffect(() => {
    if (edit || !user.adminOfSubscriberId) return;

    let isCurrent = true;
    setAreFolderStructuresLoading(true);
    getSubscriberFolderStructures(user.adminOfSubscriberId)
      .then((structures) => {
        if (!isCurrent) return;
        setFolderStructures(structures);
        setSelectedFolderStructureId(structures[0]?.id || '');
        setUseDefaultFolderStructure(structures.length > 0);
      })
      .finally(() => {
        if (isCurrent) setAreFolderStructuresLoading(false);
      });
    // eslint-disable-next-line consistent-return
    return () => {
      isCurrent = false;
    };
  }, [edit, user.adminOfSubscriberId]);

  useEffect(() => {
    if (edit || !user.adminOfSubscriberId) return;
    getSubscriberById(user.adminOfSubscriberId).then((subscriber) =>
      setWorkingDaySettings(draftFromWorkingDaySettings(subscriber)),
    );
  }, [edit, user.adminOfSubscriberId]);

  useEffect(() => {
    setProjectNameError(hasDuplicateName ? 'Project name already exists.' : undefined);
  }, [hasDuplicateName]);

  useEffect(() => {
    setEmailSlugError(hasDuplicateSlug ? 'Project email address already exists.' : undefined);
  }, [hasDuplicateSlug]);

  /**
   * A project's location lives in the address row `addressId` points at, and an address row is
   * never edited in place -- the bid setup form has always inserted a new one -- so a changed
   * location means a new row. Returns undefined when nothing changed, leaving addressId alone.
   */
  const saveLocation = async (existingAddress?: IAddress | null) => {
    const trimmedAddress1 = address1.trim();
    const trimmedCity = cityName.trim();
    const isUnchanged =
      (existingAddress?.address1 || '') === trimmedAddress1 &&
      (existingAddress?.city || '') === trimmedCity &&
      (existingAddress?.stateOrProvince?.name || '') === stateLocation;
    if (!stateLocation || isUnchanged) return undefined;

    const address = await insertAddress({
      address1: trimmedAddress1,
      address2: '',
      city: trimmedCity,
      postalCode: '',
      // insertAddress takes the state's name here and resolves it to an id.
      stateOrProvinceId: stateLocation,
    });
    return address.id;
  };

  const addProject = async (e: React.FormEvent, shouldRedirect: boolean) => {
    e.preventDefault();
    dispatch(blockNavigation());

    if (hasDuplicateSlug) return;

    setProjectNameError(undefined);
    setEmailSlugError(undefined);
    setIsLoading(true);

    setLoadingMessage(currentProject ? 'Updating Project' : 'Creating Project');

    try {
      // Update already existing project
      let newOrPatchedProject: IProject;
      if (currentProject) {
        if (!isAdminUser) {
          newOrPatchedProject = currentProject;
        } else {
          newOrPatchedProject = await patchProject(currentProject.id, {
            name: removeNewlines(projectName),
            description: projectDescription,
            ownerProjectNumber,
            architectProjectNumber,
            client,
            approvedDate: approvedDate ? approvedDate.format('YYYY-MM-DD') : undefined,
            emailSlug,
            areEmailsSuspended,
            addressId: await saveLocation(currentProject.address),
          });
        }
      } else {
        const project: ICustomInsertionProject = {
          name: removeNewlines(projectName!)!,
          description: projectDescription?.trim(),
          owner: currentProject ? undefined : projectOwner,
          ownerProjectNumber,
          architectProjectNumber,
          client,
          approvedDate: approvedDate ? approvedDate.format('YYYY-MM-DD') : undefined,
          emailSlug,
          addressId: await saveLocation(),
          folderStructureId:
            useDefaultFolderStructure && selectedFolderStructure
              ? selectedFolderStructure.id
              : undefined,
          ...(workingDaySettings ? workingDaySettingsFromDraft(workingDaySettings) : {}),
        };
        newOrPatchedProject = await insertProject(project);
      }

      // Add new users
      const newUsers = users.filter((x) => x.action === UserActionType.Add).map((x) => x.user);
      const removeUsers = users
        .filter((x) => x.action === UserActionType.Delete)
        .map((x) => x.user);
      const editUsers = users.filter((x) => x.action === UserActionType.Edit).map((x) => x.user);
      const inviteUsers = users
        .filter((x) => x.action === UserActionType.Invite)
        .map((x) => x.user);

      //Users who don't already exist in CENTERLINE and must be invited and added to the database
      setLoadingMessage('Adding Project Users');
      await Promise.all(
        inviteUsers.map((projectUser) =>
          insertUser(
            {
              email: projectUser!.user!.email,
              name: projectUser!.user!.name,
              companyId: projectUser!.user!.companyId,
            },
            newOrPatchedProject.id || '',
            projectUser.securityGroupId || '',
          ).then((newUser) => {
            insertPhoneNumberByUserId(newUser.id, {
              number: projectUser!.user!.phoneNumbers![0].number,
              type: PhoneNumberType.Mobile,
              countryId: '',
            });
          }),
        ),
      );

      // Users who do exist in CENTERLINE but not in this project
      setLoadingMessage('Updating Project Users');
      await Promise.all(
        newUsers.map((projectUser) =>
          addOrModifyUsersOfProject(newOrPatchedProject.id, [
            {
              securityGroupId: projectUser.securityGroupId || undefined,
              userId: projectUser.user!.id!,
            },
          ]),
        ),
      );

      // Removing users
      await Promise.all(
        removeUsers.map((projectUser) =>
          removeUserFromProjectByProjectId(newOrPatchedProject.id, projectUser.user!.id),
        ),
      );

      // Editing users
      await Promise.all(
        editUsers.map((projectUser) =>
          addOrModifyUsersOfProject(newOrPatchedProject.id, [
            { securityGroupId: projectUser.securityGroupId!, userId: projectUser.user!.id! },
          ]),
        ),
      );

      dispatch(allowNavigation());
      dispatch(fetchUser());
      dispatch(fetchSecurityGroups(user.id));
      dispatch(fetchProject(newOrPatchedProject.id));
      dispatch(fetchProjects()).then(() => {
        setIsLoading(false);
        if (!shouldRedirect) {
          dispatch(addSnackbar({ id: Date.now(), message: 'Saved changes!', severity: 'success' }));
        }
        history.push(`/main/projects/${newOrPatchedProject.id}${!shouldRedirect ? '/manage' : ''}`);
      });
    } catch (e: any) {
      setIsLoading(false);
      dispatch(allowNavigation());
    }
  };

  const handleDeleteUser = (id: string) => {
    const newUsers = [...users];

    const index = newUsers.findIndex((x) => x.user!.user!.id === id);
    //  If the user has just been added but the user hasn't pressed submit, simply remove the user from the user list.
    if (
      newUsers[index].action === UserActionType.Add ||
      newUsers[index].action === UserActionType.Invite
    ) {
      newUsers.splice(index, 1);
    } else {
      /* If the deletion above hasn't occurred, then that means the user is trying to delete
    a user that already exists in the database. We need to add a delete action to know to remove them
    from the database when the user submits the form */
      newUsers[index].action = UserActionType.Delete;
    }

    setUsers(newUsers);
  };

  const createProjectUser = (projectUser: IProjectUser, action = UserActionType.Add) => {
    const newUsers = [...users];
    newUsers.push({ user: projectUser, action });
    setUsers(newUsers);
  };

  const createProjectUsers = (projectUsers: IProjectUser[], action = UserActionType.Add) => {
    const newUsers = [...users];
    projectUsers.forEach((projectUser) => {
      newUsers.push({ user: projectUser, action });
    });
    setUsers(newUsers);
  };

  /* eslint-disable */
  const updateUser = (
    id: string,
    name: string | undefined,
    description: string | undefined,
    email: string | undefined,
    website: string | undefined,
    securityGroupId: string | undefined,
    securityGroupName: string | undefined,
  ) => {
    const newUsers = [...users];
    const u = newUsers.find((x) => x.user.user!.id === id);
    if (u) {
      u.user.securityGroupId = securityGroupId;

      // This info is wrong, but only the above user id is needed in the API call
      u.user.securityGroup = {
        id: securityGroupId!,
        name: securityGroupName || 'N/A',
        lastUpdatedByUserId: user.id,
        subscriberId: user.adminOfSubscriberId!,
      };

      if (u.action !== UserActionType.Add && u.action !== UserActionType.Invite) {
        u.action = UserActionType.Edit;
      }
      setUsers(newUsers);
    }
  };

  const getSuggestedEmailSlug = async () => {
    if (!projectName) {
      dispatch(
        addSnackbar({
          id: Date.now(),
          message: 'Please set a project name before generating an email slug',
          severity: 'warning',
        }),
      );
    } else {
      setHasDuplicateSlug(false);
      const slug = await getSuggestedProjectEmailSlug(projectName, emailSlug);
      setEmailSlug(slug);
    }
  };

  useEffect(() => {
    if (user && !edit) {
      const me = { ...user };
      // me.id = 'me';
      setUsers([
        {
          // @ts-ignore
          user: { user: me },
          action: UserActionType.None,
        },
        ...projectUsers
          .filter((x) => !x.user?.isSiteAdmin)
          .map((x) => {
            return { user: x, action: UserActionType.None };
          }),
      ]);
    } else
      setUsers(
        projectUsers
          .filter((x) => !x.user?.isSiteAdmin)
          .map((x) => {
            return { user: x, action: UserActionType.None };
          }),
      );
  }, [user, projectUsers]);

  useEffect(() => {
    if (params.projectId && (!currentProject || currentProject.id !== params.projectId))
      dispatch(fetchProject(params.projectId));
  }, [params.projectId, currentProject]);

  useEffect(() => {
    if (manageCurrentProject) {
      setProjectName(manageCurrentProject.name);
      setOwnerProjectNumber(manageCurrentProject.ownerProjectNumber || '');
      setArchitectProjectNumber(manageCurrentProject.architectProjectNumber || '');
      setClient(manageCurrentProject.client || '');
      setProjectDescription(manageCurrentProject.description || '');
      setApprovedDate(
        manageCurrentProject.approvedDate ? parseDate(manageCurrentProject.approvedDate) : dayjs(),
      );
      setEmailSlug(manageCurrentProject.emailSlug || '');
      setAreEmailsSuspended(!!manageCurrentProject.areEmailsSuspended);
      setAddress1(manageCurrentProject.address?.address1 || '');
      setCityName(manageCurrentProject.address?.city || '');
      setStateLocation(manageCurrentProject.address?.stateOrProvince?.name || '');
    }
  }, [manageCurrentProject]);

  const handleChangeSubscriber = async () => {
    if (!selectedSubscriberId || !canChangeSubscriber) return;

    try {
      setChangeSubscriberLoading(true);
      await patchProject(currentProject!.id, { subscriberId: selectedSubscriberId });
      window.location.reload();
    } finally {
      setChangeSubscriberLoading(false);
    }
  };

  return (
    <main className={classes.root}>
      <NavAppbar />
      <Grid container justify="center">
        <Grid item xs={12}>
          <PageTitle title={edit ? 'Manage Project' : 'Create New Project'} />
        </Grid>
      </Grid>
      <Grid container spacing={3} justify="center">
        <Grid item xs={12} container zeroMinWidth>
          {(edit && currentProject) || (!edit && !currentProject) ? (
            <Card className={classes.paper}>
              <MuiPickersUtilsProvider utils={DayjsUtils}>
                <form className={classes.form}>
                  <div
                    style={{
                      maxWidth: edit ? 'unset' : 700,
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography className={classes.title} variant="h2">
                          STEP 1. SETUP PROJECT DETAILS
                        </Typography>
                      </Grid>
                      <Grid item sm={edit ? 4 : 6} xs={12}>
                        <div className={classes.column}>
                          <FormControl className={classes.formControl}>
                            <Typography variant="body1">
                              Project Name <span style={{ color: 'red' }}>*</span>
                            </Typography>
                            <TextField
                              variant="outlined"
                              fullWidth
                              id="project-name"
                              placeholder="Project Name"
                              name="Project Name"
                              required
                              autoFocus
                              className={classes.textField}
                              disabled={!isAdminUser}
                              value={projectName}
                              error={hasDuplicateName}
                              helperText={projectNameError}
                              InputLabelProps={{
                                shrink: true,
                              }}
                              onChange={(e) => setProjectName(e.target.value)}
                            />
                          </FormControl>
                          <FormControl className={classes.formControl}>
                            <Typography variant="body1">Description</Typography>
                            <TextField
                              variant="outlined"
                              fullWidth
                              id="description"
                              placeholder="Project Description"
                              name="Description"
                              disabled={!isAdminUser}
                              value={projectDescription}
                              multiline
                              rows={5}
                              className={classes.textField}
                              onChange={(e) => setProjectDescription(e.target.value)}
                            />
                          </FormControl>
                          <FormControl className={classes.formControl}>
                            <Typography variant="body1">Project Site Address</Typography>
                            <TextField
                              variant="outlined"
                              fullWidth
                              id="address"
                              placeholder="Project Site Address"
                              name="Address"
                              disabled={!isAdminUser}
                              value={address1}
                              className={classes.textField}
                              onChange={(e) => setAddress1(removeNewlines(e.target.value) || '')}
                            />
                          </FormControl>
                          <FormControl className={classes.formControl}>
                            <Typography variant="body1">City</Typography>
                            <TextField
                              variant="outlined"
                              fullWidth
                              id="city"
                              placeholder="City"
                              name="City"
                              disabled={!isAdminUser}
                              value={cityName}
                              className={classes.textField}
                              onChange={(e) => setCityName(removeNewlines(e.target.value) || '')}
                            />
                          </FormControl>
                          <FormControl className={classes.formControl}>
                            <Typography variant="body1">
                              State <span style={{ color: 'red' }}>*</span>
                            </Typography>
                            <Select
                              id="state"
                              variant="outlined"
                              displayEmpty
                              required
                              disabled={!isAdminUser}
                              value={stateLocation}
                              className={classes.textField}
                              onChange={(e) => setStateLocation(e.target.value as string)}
                            >
                              <MenuItem value="" disabled>
                                Select State
                              </MenuItem>
                              {getStateAndProvinceItems()}
                            </Select>
                          </FormControl>
                          <FormControl className={classes.formControl} style={{ marginTop: 8 }}>
                            <div
                              style={{
                                maxWidth: 300,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Typography variant="body1">Project Email Address</Typography>
                              <SubmitButton
                                disabled={
                                  isArchivedProject || !projectName || !!currentProject?.emailSlug
                                }
                                title={isArchivedProject ? ARCHIVED_PROJECT_TOOLTIP : undefined}
                                onClick={getSuggestedEmailSlug}
                                style={{ marginLeft: 8 }}
                              >
                                Auto-generate
                              </SubmitButton>
                            </div>
                            <TextField
                              variant="outlined"
                              fullWidth
                              name="Project Email Address"
                              placeholder="customize..."
                              required
                              className={classes.textField}
                              disabled={isArchivedProject || !!currentProject?.emailSlug}
                              value={emailSlug}
                              helperText={
                                hasDuplicateSlug
                                  ? emailSlugError
                                  : 'Project email address cannot be changed once set.'
                              }
                              FormHelperTextProps={{ style: { marginLeft: 4, marginRight: 4 } }}
                              error={hasDuplicateSlug}
                              InputLabelProps={{
                                shrink: true,
                              }}
                              InputProps={{
                                endAdornment: <strong>@p.centerline.co</strong>,
                              }}
                              onChange={(e) =>
                                setEmailSlug(
                                  e.target.value.replace(/[^0-9a-zA-Z-._]+/, '').toLowerCase(),
                                )
                              }
                              onBlur={verifyEmailSlug}
                            />
                          </FormControl>
                        </div>
                      </Grid>
                      <Grid item sm={edit ? 4 : 6} xs={12}>
                        <div className={classes.column}>
                          <FormControl>
                            <Typography variant="body1">Created by</Typography>
                            <TextField
                              variant="outlined"
                              fullWidth
                              id="created-by"
                              placeholder="N/A"
                              name="Created By"
                              disabled
                              className={classes.textField}
                              value={projectOwner}
                              InputLabelProps={{
                                style: { background: 'transparent' },
                              }}
                            />
                          </FormControl>
                          <FormControl>
                            <Typography variant="body1">Created On</Typography>
                            <TextField
                              variant="outlined"
                              fullWidth
                              id="created-on"
                              placeholder="Created on"
                              name="Created on"
                              disabled
                              className={classes.textField}
                              value={date}
                              InputLabelProps={{
                                style: { background: 'transparent' },
                              }}
                            />
                          </FormControl>
                          <FormControl>
                            <Typography variant="body1">Owner&apos;s Project Number</Typography>
                            <TextField
                              variant="outlined"
                              fullWidth
                              id="owner-project-number"
                              className={classes.textField}
                              placeholder="Owner's Project Number"
                              name="Owner's Project Number"
                              disabled={!isAdminUser}
                              value={ownerProjectNumber}
                              onChange={(e) => setOwnerProjectNumber(e.target.value)}
                            />
                          </FormControl>
                          <FormControl>
                            <Typography variant="body1">Architect&apos;s Project Number</Typography>
                            <TextField
                              variant="outlined"
                              fullWidth
                              id="architect-project-number"
                              placeholder="Architect's Project Number"
                              name="Architect's Project Number"
                              className={classes.textField}
                              disabled={!isAdminUser}
                              value={architectProjectNumber}
                              onChange={(e) => setArchitectProjectNumber(e.target.value)}
                            />
                          </FormControl>
                          <FormControl>
                            <Typography variant="body1">Client</Typography>
                            <Autocomplete
                              freeSolo
                              disableClearable
                              fullWidth
                              disabled={!isAdminUser}
                              options={clientOptions}
                              classes={{ popper: classes.clientPopper }}
                              inputValue={client || ''}
                              onInputChange={(_event, value) => setClient(value)}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="outlined"
                                  id="client"
                                  placeholder="Client"
                                  name="Client"
                                  className={classes.textField}
                                  inputProps={{
                                    ...params.inputProps,
                                    style: { padding: '9.5px 0px' },
                                  }}
                                />
                              )}
                            />
                          </FormControl>
                        </div>
                      </Grid>
                      {/* Creating: this column only holds the default folder structure controls,
                          so give them the full row rather than a third of it. */}
                      <Grid item sm={edit ? 4 : 12} xs={12}>
                        <div className={classes.column}>
                          {canChangeSubscriber ? (
                            <>
                              <FormControl
                                className={classes.formControl}
                                style={{ maxWidth: 300 }}
                              >
                                <Typography variant="body1" style={{ marginBottom: 12 }}>
                                  Current Subscriber:{' '}
                                  {
                                    subscribers.find((s) => s.id === currentProject?.subscriberId)
                                      ?.name
                                  }
                                </Typography>
                                <Typography variant="body1" style={{ marginBottom: 4 }}>
                                  Change Subscriber
                                </Typography>
                                <Select
                                  variant="outlined"
                                  displayEmpty
                                  value={selectedSubscriberId}
                                  onChange={(event) =>
                                    setSelectedSubscriberId(event.target.value as string)
                                  }
                                >
                                  <MenuItem key="none" value="" disabled>
                                    Select New Subscriber
                                  </MenuItem>
                                  {subscribers
                                    .filter((s) => s.id !== currentProject?.subscriberId)
                                    .map((s) => (
                                      <MenuItem key={s.id} value={s.id}>
                                        {s.name}
                                      </MenuItem>
                                    ))}
                                </Select>
                              </FormControl>
                              {!changeSubscriberLoading ? (
                                <SubmitButton
                                  disabled={!selectedSubscriberId}
                                  onClick={handleChangeSubscriber}
                                  style={{ marginTop: 12, width: 180 }}
                                >
                                  Change Subscriber
                                </SubmitButton>
                              ) : (
                                <CircularLoader style={{ marginTop: 12, width: 180 }} />
                              )}
                            </>
                          ) : null}
                          {edit ? (
                            <>
                              <FormControlLabel
                                control={
                                  <GreenCheck
                                    checked={areEmailsSuspended}
                                    onChange={(e) => setAreEmailsSuspended(e.target.checked)}
                                    color="primary"
                                  />
                                }
                                disabled={!isAdminUser}
                                label="Suspend Email Delivery"
                                style={{ marginTop: canChangeSubscriber ? 32 : 0 }}
                              />
                              {isAdminUser ? (
                                <Typography
                                  variant="body2"
                                  style={{ fontWeight: 400, color: 'rgba(0, 0, 0, 0.38)' }}
                                >
                                  You must click Save to apply this setting
                                </Typography>
                              ) : null}
                            </>
                          ) : null}
                          {!edit &&
                          user.adminOfSubscriberId &&
                          !areFolderStructuresLoading &&
                          folderStructures.length > 0 ? (
                            <>
                              <Typography style={{ fontSize: 15, fontWeight: 600, marginTop: 16 }}>
                                CENTERLINE DRIVE SETUP
                              </Typography>
                              <div
                                style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
                              >
                                <FormControlLabel
                                  control={
                                    <GreenCheck
                                      id="use-default-folder-structure"
                                      checked={useDefaultFolderStructure}
                                      onChange={(e) =>
                                        setUseDefaultFolderStructure(e.target.checked)
                                      }
                                      color="primary"
                                    />
                                  }
                                  label="Use Default Folder Structure"
                                />
                                <Select
                                  variant="outlined"
                                  displayEmpty
                                  margin="dense"
                                  inputProps={{ 'aria-label': 'Folder Structure' }}
                                  disabled={!useDefaultFolderStructure}
                                  value={selectedFolderStructureId}
                                  onChange={(event) =>
                                    setSelectedFolderStructureId(event.target.value as string)
                                  }
                                  style={{ minWidth: 240, marginRight: 8 }}
                                >
                                  <MenuItem key="none" value="" disabled>
                                    Select Folder Structure
                                  </MenuItem>
                                  {folderStructures.map((structure) => (
                                    <MenuItem key={structure.id} value={structure.id}>
                                      {structure.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  startIcon={<FolderOpenOutlined />}
                                  disabled={!selectedFolderStructure}
                                  onClick={() => setIsFolderStructureDialogOpen(true)}
                                  style={{ maxWidth: 300 }}
                                >
                                  View Structure
                                </Button>
                              </div>
                            </>
                          ) : null}
                        </div>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider light />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2">Dates</Typography>
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <div className={classes.column}>
                          <FormControl className={classes.formControl}>
                            <Typography variant="body1">Approved Date</Typography>
                            <KeyboardDatePicker
                              disabled={!isAdminUser}
                              value={approvedDate}
                              id="approved-date"
                              onChange={(d) => setApprovedDate(d)}
                              inputVariant="outlined"
                              InputProps={{ style: { padding: 0 } }}
                              format="MM/DD/YYYY"
                              placeholder="Approved Date"
                              className={classes.textField}
                            />
                          </FormControl>
                        </div>
                      </Grid>
                      {!edit && workingDaySettings ? (
                        <>
                          <Grid item xs={12}>
                            <Divider light />
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2">Review Deadlines</Typography>
                            <Typography variant="body2">
                              Defaulted from your subscriber settings &mdash; adjust them here if
                              this project&apos;s contract counts review periods differently.
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <WorkingDaysSettingsFields
                              value={workingDaySettings}
                              onChange={setWorkingDaySettings}
                              disabled={!isAdminUser}
                            />
                          </Grid>
                        </>
                      ) : null}
                      <Grid item xs={12} alignItems="flex-start">
                        {showStateRequired ? (
                          <div className={classes.stateRequiredPill}>State is required</div>
                        ) : null}
                        {!isLoading ? (
                          <TooltipIfDisabledComponent
                            disabled={isArchivedProject}
                            title={ARCHIVED_PROJECT_TOOLTIP}
                            spanStyle={{ width: 'auto', textAlign: 'left' }}
                          >
                            <SubmitButton
                              id="submit-button"
                              onClick={(e) => addProject(e, false)}
                              disabled={
                                isArchivedProject ||
                                !projectName ||
                                hasDuplicateName ||
                                hasDuplicateSlug ||
                                !!workingDaySettingsError ||
                                (!!isAdminUser && !stateLocation)
                              }
                              variant="contained"
                              color="primary"
                              startIcon={<Save />}
                              className={classes.submit}
                              style={{ marginLeft: 0 }}
                            >
                              Save
                            </SubmitButton>
                          </TooltipIfDisabledComponent>
                        ) : (
                          <CircularLoader
                            style={{ justifyContent: 'flex-start', marginLeft: 32 }}
                          />
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                    </Grid>
                  </div>
                  <div style={{ maxWidth: 1600, marginTop: 16 }}>
                    <CreateProjectUsers
                      label="STEP 2. MANAGE PROJECT USERS"
                      users={users
                        .filter((x) => x.action !== UserActionType.Delete)
                        .map((x) => {
                          return { ...x.user, isInvited: x.action !== UserActionType.Invite };
                        })}
                      createProjectUser={createProjectUser}
                      createProjectUsers={createProjectUsers}
                      handleDeleteUser={handleDeleteUser}
                      updateUser={updateUser}
                    />
                  </div>

                  <div className={classes.widthConstraint}>
                    <Grid container spacing={2}>
                      {/* <Grid item xs={12}>
                      <Divider />
                    </Grid> */}
                      <Grid item xs={12} style={{ marginTop: 16 }}>
                        <Divider />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography className={classes.title} variant="h2">
                          STEP 3.{' '}
                          {edit
                            ? 'UPDATE PROJECT DETAILS AND INVITE USERS'
                            : 'COMPLETE PROJECT SETUP AND INVITE USERS'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <div className={classes.endDiv}>
                          {isLoading ? (
                            <div className={classes.loadingCenter}>
                              <div className={classes.loadingMargin}>
                                <CircularLoader />
                              </div>
                              <div style={{ alignSelf: 'center' }}>{loadingMessage}</div>
                            </div>
                          ) : (
                            <div style={{ alignSelf: 'flex-start' }}>
                              {showStateRequired ? (
                                <div
                                  className={`${classes.stateRequiredPill} ${classes.stateRequiredPillInset}`}
                                >
                                  State is required
                                </div>
                              ) : null}
                              <TooltipIfDisabledComponent
                                disabled={isArchivedProject}
                                title={ARCHIVED_PROJECT_TOOLTIP}
                                spanStyle={{ width: 'auto', textAlign: 'left' }}
                              >
                                <SubmitButton
                                  id="submit-button"
                                  onClick={(e) => addProject(e, true)}
                                  disabled={
                                    isArchivedProject ||
                                    !projectName ||
                                    hasDuplicateName ||
                                    hasDuplicateSlug ||
                                    !!workingDaySettingsError ||
                                    (!!isAdminUser && !stateLocation)
                                  }
                                  variant="contained"
                                  color="primary"
                                  className={classes.submit}
                                >
                                  <Add />
                                  {edit
                                    ? 'Update Project Details and invite Users'
                                    : 'Complete project setup and invite users'}
                                </SubmitButton>
                              </TooltipIfDisabledComponent>
                            </div>
                          )}
                        </div>
                      </Grid>
                    </Grid>
                  </div>
                </form>
              </MuiPickersUtilsProvider>
            </Card>
          ) : (
            <FullscreenLoader />
          )}
        </Grid>
      </Grid>
      <DefaultFolderStructureDialog
        open={isFolderStructureDialogOpen}
        handleClose={() => setIsFolderStructureDialogOpen(false)}
        structure={selectedFolderStructure}
        projectName={projectName?.trim()}
        isLoading={areFolderStructuresLoading}
      />
    </main>
  );
}

export default AddOrManageProjectPage;
